import Image from "next/legacy/image"
import Block from "@/components/wrappers/block"
import { ImagePlaceholder } from "@/components/blocks/theme/wild-neighbors-single/image-placeholder"
import { Icon } from "@/components/blocks/theme/wild-neighbors-register/icon"
import GoogleMapReact from "google-map-react"
import styles from "./flowerfieldMap.module.css"
import { Fragment, useEffect, useState } from "react"
import Close from "@/common/icons/close"
import classNames from "classnames"
import mapStyles from "./customMapsStyle"
import { A11y, Navigation } from "swiper/modules"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import Arrow from "@/common/icons/arrow"
import swiperStyle from "@/components/blocks/theme/wild-neighbors-map/swiper.module.css"

function ActivitySwiper({ activity }) {
  const [swiper, setSwiper] = useState(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  if (!mounted) return null

  return (
    <Swiper
      className={swiperStyle.swiper}
      modules={[Navigation, A11y]}
      slidesPerView={1}
      onSwiper={setSwiper}>
      {activity.files.map((image, index) => (
        <SwiperSlide key={index}>
          <figure className="relative aspect-w-4 aspect-h-3">
            <Image
              src={image.url}
              alt={image?.title || "Bild"}
              layout="fill"
              objectFit="cover"
            />
          </figure>
        </SwiperSlide>
      ))}
      <button
        type="button"
        aria-label="Föregående"
        onClick={() => swiper?.slidePrev()}
        className="absolute z-10 flex items-center justify-center w-10 h-10 transition-all bg-white rounded-full swiper-nav bottom-4 left-4">
        <Arrow className="w-5 h-auto transform rotate-180" />
      </button>
      <button
        type="button"
        aria-label="Nästa"
        onClick={() => swiper?.slideNext()}
        className="absolute z-10 flex items-center justify-center w-10 h-10 transition-all bg-white rounded-full swiper-nav bottom-4 right-4">
        <Arrow className="w-5 h-auto" />
      </button>
    </Swiper>
  )
}

export default function FlowerfieldSingle({ block }) {
  const mapKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY
  const typeNames = {
    private: "Enskild vägkant",
    municipal: "Kommunal vägkant",
    state: "Statlig vägkant"
  }
  const [selectedMarker, setSelectedMarker] = useState(null)
  const species = block.attrs.species
  const strokeColors = {
    started: "#f0e100",
    living: "#68a635",
    demo: "#68a53f"
  }

  const handleApiLoaded = (map, maps, key, distance) => {
    // Add drawing mode to the map
    let drawingManager = new maps.drawing.DrawingManager({
      drawingMode: maps.drawing.OverlayType.POLYLINE
    })

    // Draw existing polyline to the map
    let savedCoordinates = JSON.parse(distance.coordinates.coordinates)

    let savedLine = new maps.Polyline({
      path: savedCoordinates,
      geodesic: true,
      strokeColor: strokeColors[distance.status] ?? "#fff",
      strokeOpacity: 0.9,
      strokeWeight: 4
    })

    savedLine.setMap(map)
    savedLine.setEditable(false)

    drawingManager.setOptions({
      drawingControl: false,
      drawingMode: null,
      drawingControlOptions: {
        drawingModes: []
      }
    })
    drawingManager.setMap(map)

    // Add markers
    distance.speciesToShowOnMap.forEach((specie) => {
      specie.markers.forEach((marker) => {
        let markerOnMap = new window.google.maps.Marker({
          position: new window.google.maps.LatLng(marker.lat, marker.lng),
          map: map
        })

        window.google.maps.event.addListener(markerOnMap, "click", function () {
          setSelectedMarker(specie)
        })
      })
    })
  }

  const Card = ({ marker }) => {
    if (marker === null) {
      return null
    }

    let specieInfo = species.find((item) => {
      return item.specie_key == marker.specie_key
    })

    if (!specieInfo) {
      return null
    }

    return (
      <div className={styles.cardHolder}>
        <div className={styles.card}>
          <div
            onClick={() => setSelectedMarker(null)}
            className={classNames(styles.hideBtn)}>
            <Close className={classNames("close", "black")} />
          </div>
          {specieInfo?.specie_image && (
            <figure className="relative aspect-w-4 aspect-h-3">
              <img src={specieInfo.specie_image} />
            </figure>
          )}
          <div className="p-4 text-base">
            <h4>
              {specieInfo.specie_name} ({specieInfo.specie_latin_name})
            </h4>
            <p>{specieInfo.specie_description}</p>
            {specieInfo.specie_red_listed && <>Rödlistad</>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Block block={block}>
        {block.attrs.info.distances.map((distance, index) => {
          let coordinates = distance?.coordinates?.coordinates
          let showMap = true

          if (coordinates) {
            coordinates = JSON.parse(distance.coordinates.coordinates)
          } else {
            showMap = false
            coordinates = [
              {
                lat: 0,
                lng: 0
              }
            ]
          }

          return (
            <div key={index} className="mb-10">
              <h1 className="mb-4">{distance.title}</h1>
              <p className="font-serif text-2xl">
                {typeNames[distance.type]}
                <br />
                {distance.hasOrganization && distance.organization && (
                  <>
                    Vägsträckan ingår i {distance.organization}
                    <br />
                  </>
                )}
                {(distance.coordinates.meters / 1000).toFixed(2)} KM
                <br />
              </p>

              {distance.content !== "" && (
                <>
                  <h2 className="mb-4 mt-15">Information</h2>
                  <p className="preamble">{distance.description}</p>
                </>
              )}

              {distance.media.length > 0 && (
                <>
                  <h2 className="mb-4 mt-15">Bilder</h2>
                  {distance.media.map((image, index) => {
                    return (
                      <Fragment key={index}>
                        <img className={styles.distanceImage} src={image} />
                      </Fragment>
                    )
                  })}
                </>
              )}

              {distance.activities.length > 0 && (
                <>
                  <div className="flex items-center mb-10 mt-15">
                    <h2>Våra aktiviteter</h2>
                    <span className="block px-4 py-2 ml-4 bg-summer-green-50 rounded-[32px]">
                      {distance.activities.length} st
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 md:gap-10 lg:grid-cols-3">
                    {distance.activities.map((activity, index) => {
                      return (
                        <div key={index}>
                          <div className="rounded-[4px] overflow-hidden mb-10">
                            {activity.files.length > 0 ? (
                              <ActivitySwiper activity={activity} />
                            ) : (
                              <figure className="relative aspect-w-4 aspect-h-3">
                                <ImagePlaceholder className="object-cover" />
                              </figure>
                            )}
                            <div className="p-4 bg-white">
                              <div className="flex items-start gap-4 mb-2">
                                <div>
                                  <Icon
                                    svg={
                                      block.attrs.activities[activity.activity]
                                        .icon.content
                                    }
                                  />
                                </div>
                                <div>
                                  <h5 className="mb-2 font-sans font-medium">
                                    {
                                      block.attrs.activities[activity.activity]
                                        .activity_name
                                    }
                                  </h5>
                                  <span className="block text-xs leading-3 font-bold uppercase tracking-[1px]">
                                    {activity.status === "completed" ? (
                                      <>
                                        <span className="inline-block mr-2 rounded-full w-3 h-3 bg-[#F8AF8F]"></span>
                                        <span className="relative -top-[1px]">
                                          Planerad
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="inline-block w-3 h-3 mr-2 rounded-full bg-summer-green-100"></span>
                                        <span className="relative -top-[1px]">
                                          Genomförd
                                        </span>
                                      </>
                                    )}
                                  </span>
                                </div>
                              </div>
                              <p className="text-base">
                                {activity.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
              {showMap && (
                <>
                  <h2 className="mb-10 mt-15">
                    Karta med artfynd och skötselåtgärder
                  </h2>
                  <div className="w-full h-[800px]">
                    <GoogleMapReact
                      yesIWantToUseGoogleMapApiInternals
                      key={distance.id}
                      className="relative"
                      bootstrapURLKeys={{
                        key: mapKey,
                        libraries: ["drawing"]
                      }}
                      center={{
                        lat: coordinates[0].lat,
                        lng: coordinates[0].lng
                      }}
                      zoom={12}
                      onGoogleApiLoaded={({ map, maps }) =>
                        handleApiLoaded(map, maps, index, distance)
                      }
                      options={{
                        styles: mapStyles,
                        fullscreenControl: true,
                        zoomControl: true,
                        scrollwheel: true,
                        disableDefaultUI: true,
                        mapTypeId: "hybrid",
                        mapTypeControl: true
                      }}
                      disableDoubleClickZoom={true}
                    />
                    <Card marker={selectedMarker} />
                  </div>
                </>
              )}
            </div>
          )
        })}
      </Block>
    </>
  )
}
